/**
 * CoverLetterPreview — renders a structured cover letter with export buttons.
 * Displays header, salutation, paragraphs (hook, valueDemo, culturalFit, closing),
 * sign-off, and signature from CoverLetterContent.
 */

import { useState } from 'react';
import { Button } from '@/components/common';
import {
  coverLetterService,
  type CoverLetterExportFormat,
} from '@/services/coverLetter.service';
import type { CoverLetterContent, CoverLetterTemplate } from '@shared/types/coverLetter';

interface CoverLetterPreviewProps {
  content: CoverLetterContent;
  letterText: string;
  template: CoverLetterTemplate;
  wordCount?: number;
  atsKeywordCoverage?: number;
}

export function CoverLetterPreview({
  content,
  letterText,
  template,
  wordCount,
  atsKeywordCoverage,
}: CoverLetterPreviewProps) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (format: CoverLetterExportFormat) => {
    setIsExporting(true);
    try {
      const result = await coverLetterService.exportCoverLetter(
        content,
        letterText,
        format,
        template
      );
      coverLetterService.downloadFile(result.buffer, result.filename);
    } catch (err) {
      console.error('Cover letter export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const { header, salutation, paragraphs, signOff, signature } = content;

  return (
    <div className="cl-preview">
      {/* Stats bar */}
      {(wordCount || atsKeywordCoverage !== undefined) && (
        <div className="cl-preview__stats">
          {wordCount && (
            <span className="cl-preview__stat">
              {wordCount} words
            </span>
          )}
          {atsKeywordCoverage !== undefined && (
            <span className="cl-preview__stat">
              {Math.round(atsKeywordCoverage * 100)}% keyword match
            </span>
          )}
        </div>
      )}

      {/* Letter content */}
      <div className="cl-preview__letter">
        {/* Header */}
        <div className="cl-preview__header">
          <p className="cl-preview__name">{header.candidateName}</p>
          {header.candidateContact && (
            <p className="cl-preview__contact">{header.candidateContact}</p>
          )}
          <p className="cl-preview__date">{header.date}</p>
        </div>

        {/* Recipient */}
        <div className="cl-preview__recipient">
          {header.recipientName && <p>{header.recipientName}</p>}
          {header.recipientTitle && <p>{header.recipientTitle}</p>}
          <p>{header.companyName}</p>
          {header.companyAddress && <p>{header.companyAddress}</p>}
        </div>

        {/* Salutation */}
        <p className="cl-preview__salutation">{salutation}</p>

        {/* Body paragraphs */}
        <div className="cl-preview__body">
          <p>{paragraphs.hook}</p>
          <p>{paragraphs.valueDemo}</p>
          <p>{paragraphs.culturalFit}</p>
          <p>{paragraphs.closing}</p>
        </div>

        {/* Sign-off */}
        <div className="cl-preview__signoff">
          <p>{signOff}</p>
          <p className="cl-preview__signature">{signature}</p>
        </div>
      </div>

      {/* Export buttons */}
      <div className="cl-preview__export">
        <Button
          variant="primary"
          size="sm"
          onClick={() => handleExport('pdf')}
          disabled={isExporting}
        >
          {isExporting ? 'Exporting...' : 'Download PDF'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleExport('docx')}
          disabled={isExporting}
        >
          Download DOCX
        </Button>
      </div>
    </div>
  );
}
